import { useEffect, useState } from 'react';
import { BillInvoices } from './Invoices';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AGEING_LABELS, LEDGER_KIND_LABELS, type AgeingBucket, type LedgerKind } from '@shared/domain';
import { api, actionKey } from '../../api';
import type { LedgerEntry, OpenItem, PaymentDetails, RelSummary, Statement } from '../../types';
import { Badge, ConfirmDialog, Empty, ErrorBox, Field, Loading, RupeeInput, day, dt, inr, rupeesToPaise, statusTone, today, useBusy } from '../../ui';
import { Icon, rs } from '../../brand';
import { paiseToRupeeString } from '@shared/money';

export interface Agreement {
  _id: string;
  version: number;
  status: string;
  kind: 'FULL_TERMS' | 'LIMIT_CHANGE';
  changeReason: string;
  terms: { fullTankCeilingPaise?: string; creditLimitPaise: string; dueDays: number; graceDays: number; autoFreezeOnOverdue: boolean; requireReceiptPhoto: boolean; depositPaise: string; billingCycle: { type: string; startDay?: number; weekEndsOn?: number; anchorDate?: string; customEndDates?: string[] } };
  proposedBy: { name: string };
  proposedAt: string;
  buyerAcceptedBy?: { name: string };
  buyerAcceptedAt?: string;
}

export function cycleText(c: Agreement['terms']['billingCycle']): string {
  switch (c.type) {
    case 'WEEKLY': return `Every week, ending ${['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][c.weekEndsOn ?? 7]}`;
    case 'FORTNIGHTLY': return `Every 2 weeks from ${day(c.anchorDate)}`;
    case 'MONTHLY': return `Every month from day ${c.startDay ?? 1}`;
    default: return `On fixed dates: ${(c.customEndDates ?? []).map((d) => day(d)).join(', ')}`;
  }
}

export function TermsTable({ t }: { t: Agreement['terms'] }) {
  return (
    <div className="summary">
      <dl>
        <dt>Credit limit</dt><dd>{inr(t.creditLimitPaise)}</dd>
        <dt>Fill-the-tank ceiling</dt><dd>{t.fullTankCeilingPaise ? `${inr(t.fullTankCeilingPaise)} per indent` : 'Not enabled'}</dd>
        <dt>Bill period</dt><dd>{cycleText(t.billingCycle)}</dd>
        <dt>Pay by</dt><dd>{t.dueDays} days after each bill period ends</dd>
        <dt>Extra days before stopping</dt><dd>{t.graceDays} days{t.autoFreezeOnOverdue ? ' (credit stops automatically after that)' : ' (no automatic stop)'}</dd>
        <dt>Receipt photo</dt><dd>{t.requireReceiptPhoto ? 'Required for each fill' : 'Optional'}</dd>
        <dt>Security deposit</dt><dd>{BigInt(t.depositPaise) > 0n ? `${inr(t.depositPaise)} (held by the supplier)` : 'None'}</dd>
      </dl>
    </div>
  );
}

export function AgreementHistory({ list, canAccept, onChanged }: { list: Agreement[]; canAccept: boolean; onChanged: () => void }) {
  const pending = list.find((a) => a.status === 'PROPOSED');
  const active = list.find((a) => a.status === 'ACTIVE');
  const [open, setOpen] = useState<'accept' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <h2>Credit terms</h2>
      {pending ? (
        <div className="alert warn">
          <strong>New terms proposed (version {pending.version}) by {pending.proposedBy.name}.</strong>
          <TermsTable t={pending.terms} />
          {canAccept ? (
            <div className="row" style={{ marginTop: '.5rem' }}>
              <button className="btn primary" onClick={() => setOpen('accept')}>Accept these terms</button>
              <button className="btn" onClick={() => setOpen('reject')}>Reject</button>
            </div>
          ) : <p>Waiting for the company owner to accept.</p>}
        </div>
      ) : null}
      {active ? (
        <>
          <p className="muted">Active: version {active.version}{active.kind === 'LIMIT_CHANGE' ? ' (limit change by supplier)' : ''}{active.buyerAcceptedBy ? ` · accepted by ${active.buyerAcceptedBy.name} on ${day(active.buyerAcceptedAt)}` : ''}</p>
          <TermsTable t={active.terms} />
        </>
      ) : !pending ? <p className="muted">No terms agreed yet.</p> : null}
      <details style={{ marginTop: '.75rem' }}>
        <summary>All versions</summary>
        <ul className="list">
          {list.map((a) => (
            <li key={a._id}>v{a.version} · <Badge tone={statusTone(a.status)}>{a.status.toLowerCase()}</Badge> · limit {inr(a.terms.creditLimitPaise)} · {a.changeReason} · <small>{a.proposedBy.name}, {dt(a.proposedAt)}</small></li>
          ))}
        </ul>
      </details>
      {pending ? (
        <ConfirmDialog open={open !== null} title={open === 'accept' ? 'Accept these credit terms?' : 'Reject these terms?'} lines={[['Credit limit', inr(pending.terms.creditLimitPaise)], ['Pay by', `${pending.terms.dueDays} days after period end`]]} confirmLabel={open === 'accept' ? 'I accept' : 'Reject'} danger={open === 'reject'} busy={busy} error={error} onClose={() => setOpen(null)} onConfirm={() => void run(async () => {
          if (open === 'accept') await api.post(`/api/agreements/${pending._id}/accept`);
          else await api.post(`/api/agreements/${pending._id}/reject`, { reason });
          setOpen(null);
          onChanged();
        })}>
          {open === 'reject' ? <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field> : <p className="muted">Your acceptance is recorded with your name and time. The supplier gives this credit; SuperIndent only keeps the records.</p>}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

export function PaySupplier({ details, supplierName }: { details?: PaymentDetails; supplierName: string }) {
  if (!details) return <div className="card"><h2>Pay the supplier</h2><p className="muted">The supplier has not added bank or UPI details yet. Ask them directly.</p></div>;
  const recentChange = Date.now() - new Date(details.updatedAt).getTime() < 14 * 86400_000 && details.version > 1;
  return (
    <div className="card">
      <h2>Pay {supplierName} directly</h2>
      <p className="muted">Pay from your bank or UPI app to the supplier’s own account below. SuperIndent never receives this money.</p>
      {details.verification.status === 'VERIFIED' ? <Badge tone="ok">Details checked by SuperIndent support</Badge> : <Badge tone="warn">Details not checked yet – confirm with the supplier by phone</Badge>}
      {recentChange ? <div className="alert warn">These details were changed on {day(details.updatedAt)}. Call the supplier on a number you already know before paying.</div> : null}
      <div className="summary" style={{ marginTop: '.75rem' }}>
        <dl>
          {details.bankAccount ? (
            <>
              <dt>Account name</dt><dd>{details.bankAccount.holderName}</dd>
              <dt>Account number</dt><dd className="mono">{details.bankAccount.accountNumber}</dd>
              <dt>IFSC</dt><dd className="mono">{details.bankAccount.ifsc}</dd>
              <dt>Bank</dt><dd>{details.bankAccount.bankName}</dd>
            </>
          ) : null}
          {details.upiId ? <><dt>UPI</dt><dd className="mono">{details.upiId}</dd></> : null}
        </dl>
      </div>
      <p>After paying, fill in <strong>I have paid</strong> with the UTR / reference number.</p>
    </div>
  );
}

export function ReportPayment({ relationshipId, side, onDone, prefill }: { relationshipId: string; side: 'buyer' | 'supplier'; onDone: () => void; prefill?: { paise: string; n: number } }) {
  const [amount, setAmount] = useState('');
  // "I have paid" on the bill card fills in the amount shown there; it stays editable.
  useEffect(() => { if (prefill?.n) setAmount(paiseToRupeeString(BigInt(prefill.paise))); }, [prefill?.n, prefill?.paise]);
  const [paidOn, setPaidOn] = useState(today());
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [ok, setOk] = useState('');
  const { busy, error, run, setError } = useBusy();
  const paise = rupeesToPaise(amount);
  return (
    <div className="card">
      <h2>{side === 'buyer' ? 'I have paid' : 'Record money received'}</h2>
      <p className="muted">{side === 'buyer' ? 'Your credit comes back only after the supplier confirms the money reached their bank.' : 'Saved as pending; confirm it after checking your bank statement.'}</p>
      <div className="grid two">
        <Field label="Amount (₹)"><RupeeInput value={amount} onChange={setAmount} /></Field>
        <Field label="Paid on"><input type="date" value={paidOn} max={today()} onChange={(e) => setPaidOn(e.target.value)} /></Field>
        <Field label="How">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="BANK_TRANSFER">Bank transfer (NEFT/RTGS/IMPS)</option>
            <option value="UPI">UPI</option>
            <option value="CHEQUE">Cheque</option>
            <option value="CASH">Cash</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>
        <Field label="UTR / reference number"><input value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      </div>
      <Field label="Proof (optional photo or PDF)"><input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
      {ok ? <div className="alert ok">{ok}</div> : null}
      <button className="btn primary" disabled={!paise || paise === '0' || !reference.trim()} onClick={() => { setError(null); setConfirm(true); }}>Continue</button>
      <ConfirmDialog open={confirm} title="Check the payment details" lines={[['Amount', inr(paise)], ['Paid on', day(paidOn)], ['Reference', reference]]} confirmLabel={side === 'buyer' ? 'Send to supplier' : 'Save as received'} busy={busy} error={error} onClose={() => setConfirm(false)} onConfirm={() => void run(async () => {
        let proofAttachmentId: string | undefined;
        if (file) {
          const fd = new FormData();
          fd.append('purpose', 'PAYMENT_PROOF');
          fd.append('file', file);
          proofAttachmentId = (await api.post<{ id: string }>('/api/attachments', fd)).id;
        }
        const k = actionKey(`payment:${relationshipId}:${paise}:${reference}`);
        await api.post(side === 'buyer' ? '/api/payments/report' : '/api/payments/record', { relationshipId, amountPaise: paise, paidOn, method, reference, proofAttachmentId }, { idempotencyKey: k.key });
        k.done();
        setConfirm(false);
        setAmount('');
        setReference('');
        setOk(side === 'buyer' ? 'Sent. It shows as “payment pending confirmation” until the supplier confirms.' : 'Saved. Confirm it from Payments.');
        onDone();
      })} />
    </div>
  );
}

interface LedgerData {
  summary: RelSummary;
  entries: LedgerEntry[];
  openItems: OpenItem[];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function addDaysIso(date: string, days: number) {
  const d = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "September bill" for a whole calendar month, otherwise "1 Sep – 15 Sep bill". */
export function billName(periodStart: string, periodEnd: string): string {
  const start = periodStart.slice(0, 10);
  const end = periodEnd.slice(0, 10);
  const wholeMonth = start.endsWith('-01') && start.slice(0, 7) === end.slice(0, 7) && addDaysIso(end, 1).endsWith('-01');
  return wholeMonth ? `${MONTHS[Number(start.slice(5, 7)) - 1]} bill` : `${day(start)} – ${day(end)} bill`;
}

/** Tabs kept in the address (?tab=) so a reload or a shared link opens the same tab. */
export function useTab(tabs: { id: string; label: string }[], fallback: string) {
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab');
  const tab = tabs.some((t) => t.id === wanted) ? wanted! : fallback;
  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };
  const bar = (
    <div className="tabs" role="tablist" aria-label="Account">
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.label}</button>
      ))}
    </div>
  );
  return { tab, setTab, bar };
}

/** Headline figures for one account. Labels follow whose side the viewer is on. */
export function AccountFigures({ s, side, hideDue }: { s: RelSummary; side: 'buyer' | 'supplier'; hideDue?: boolean }) {
  const p = s.position;
  const late = BigInt(s.overduePaise) > 0n;
  return (
    <section className="figures" aria-label="Account figures">
      <div className="stat fig dark"><span className="label">{side === 'buyer' ? 'You owe' : 'They owe you'}</span><span className="value">{inr(p.outstandingPaise)}</span></div>
      {!hideDue && s.nextDueDate && BigInt(s.nextDueAmountPaise) > 0n ? (
        <div className="stat fig amber"><span className="label">To pay · due {day(s.nextDueDate)}</span><span className="value">{inr(s.nextDueAmountPaise)}</span></div>
      ) : null}
      <div className={`stat fig ${late ? 'red' : ''}`}><span className="label">Overdue</span><span className="value">{inr(s.overduePaise)}</span></div>
      <div className="stat fig"><span className="label">Approved, not filled yet</span><span className="value">{inr(p.reservedPaise)}</span></div>
      <div className="stat fig"><span className="label">Can use now · limit {rs(p.creditLimitPaise)}</span><span className="value good">{inr(p.availablePaise)}</span></div>
    </section>
  );
}

/** Unpaid items and how soon they are due. */
export function UnpaidSection({ relationshipId }: { relationshipId: string }) {
  const q = useQuery({ queryKey: ['ledger', relationshipId], queryFn: () => api.get<LedgerData>(`/api/relationships/${relationshipId}/ledger`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const buckets = (Object.entries(d.summary.ageing) as [AgeingBucket, string][]).filter(([, v]) => BigInt(v) > 0n);
  return (
    <div className="card">
      <h2>Unpaid, and when it’s due</h2>
      {buckets.length ? (
        <div className="row" style={{ marginBottom: '.5rem' }}>
          {buckets.map(([k, v]) => <Badge key={k} tone={statusTone(k)}>{AGEING_LABELS[k]}: {inr(v)}</Badge>)}
        </div>
      ) : null}
      {!d.openItems.length ? <Empty>Nothing unpaid.</Empty> : (
        <ul className="rows flat">
          {d.openItems.map((i) => (
            <li key={i.itemId}>
              <div className="row between" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                <div>
                  <strong>{LEDGER_KIND_LABELS[i.kind as LedgerKind]}{i.manual ? ' (by hand)' : ''}</strong> <small className="muted">· {day(i.occurredAt)}</small>
                  <div><small className="muted">{i.description}</small></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="num" style={{ fontSize: '1.15rem' }}>{inr(i.unpaidPaise)}</div>
                  {i.unpaidPaise !== i.originalPaise ? <small className="muted">of {inr(i.originalPaise)}</small> : null}
                </div>
              </div>
              <div className="row" style={{ gap: '.5rem', marginTop: '.3rem' }}>
                <Badge tone={statusTone(i.bucket)}>{AGEING_LABELS[i.bucket as AgeingBucket]}</Badge>
                <small className="muted">due {day(i.dueDate)}</small>
                {i.fulfilmentId ? <Link to={`/supplies/${i.fulfilmentId}`}>See the fill</Link> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Every ledger entry, latest on top. Entries are never edited. */
export function HistorySection({ relationshipId, side }: { relationshipId: string; side: 'buyer' | 'supplier' }) {
  const q = useQuery({ queryKey: ['ledger', relationshipId], queryFn: () => api.get<LedgerData>(`/api/relationships/${relationshipId}/ledger`) });
  const [showAll, setShowAll] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const entries = showAll ? d.entries : d.entries.slice(0, 15);
  return (
    <div className="card">
      <h2>All fills, payments and corrections <small className="muted" style={{ fontWeight: 500 }}>· latest on top</small></h2>
      <p className="muted">Nothing here is ever edited. A correction or payment is added as a new line.</p>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Entry</th><th className="num">{side === 'buyer' ? 'You owe more' : 'They owe more'}</th><th className="num">{side === 'buyer' ? 'You paid' : 'They paid'}</th><th className="num">Balance</th></tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e._id}>
                <td>{dt(e.occurredAt)}</td>
                <td><strong>{LEDGER_KIND_LABELS[e.kind as LedgerKind]}</strong><div><small>{e.description} · by {e.postedBy.name}</small></div>{e.fulfilmentId ? <Link to={`/supplies/${e.fulfilmentId}`}>See the fill</Link> : null}</td>
                <td className="num">{e.direction === 'DEBIT' ? inr(e.amountPaise) : ''}</td>
                <td className="num" style={{ color: 'var(--ok)', fontWeight: 700 }}>{e.direction === 'CREDIT' ? inr(e.amountPaise) : ''}</td>
                <td className="num"><strong>{inr(e.balanceAfterPaise)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.entries.length > 15 ? <button className="btn small" onClick={() => setShowAll(!showAll)}>{showAll ? 'Show fewer' : `Show all ${d.entries.length}`}</button> : null}
    </div>
  );
}

/** Both sections together, for pages that are not split into tabs. */
export function LedgerSection({ relationshipId, side = 'supplier' }: { relationshipId: string; side?: 'buyer' | 'supplier' }) {
  return (
    <>
      <UnpaidSection relationshipId={relationshipId} />
      <HistorySection relationshipId={relationshipId} side={side} />
    </>
  );
}

export function StatementsSection({ relationshipId, canIssue, notBilledPaise }: { relationshipId: string; canIssue: boolean; notBilledPaise?: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['statements', relationshipId], queryFn: () => api.get<{ statements: Statement[]; suggestedPeriod: { start: string; end: string } | null }>(`/api/relationships/${relationshipId}/statements`) });
  const [open, setOpen] = useState(false);
  const { busy, error, run } = useBusy();
  const sp = q.data?.suggestedPeriod;
  const now = today();
  return (
    <div className="stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>Monthly bills</h2>
        {canIssue && sp ? <button className="btn" onClick={() => setOpen(true)}>Issue bill</button> : null}
      </div>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      <ul className="rows bills">
        {notBilledPaise && BigInt(notBilledPaise) > 0n ? (
          <li className="bill-row">
            <span className="bill-icon running"><Icon name="clock" /></span>
            <div className="bill-main"><strong>Fuel not billed yet</strong><small>Goes on the next bill</small></div>
            <div className="bill-amount"><span className="num">{rs(notBilledPaise)}</span><span className="pill pill-closed">Running</span></div>
          </li>
        ) : null}
        {q.data?.statements.map((s) => {
          const upcoming = s.dueDate.slice(0, 10) >= now;
          return (
            <li key={s._id} className="bill-row">
              <span className={`bill-icon ${upcoming ? 'due' : 'past'}`}><Icon name="bills" /></span>
              <div className="bill-main">
                <Link to={`/statements/${s._id}`}><strong>{billName(s.periodStart, s.periodEnd)}</strong></Link>
                <small>Bill total · {upcoming ? `pay by ${day(s.dueDate)}` : `was due ${day(s.dueDate)}`} · {s.number}{s.version > 1 ? ` · version ${s.version}` : ''}</small>
                <span className="row" style={{ gap: '.4rem', marginTop: '.2rem' }}>
                  <a className="btn small" href={`/api/statements/${s._id}/pdf`} onClick={(e) => { e.preventDefault(); void download(`/api/statements/${s._id}/pdf`, `${s.number}.pdf`); }}>PDF</a>
                  <a className="btn small" href={`/api/statements/${s._id}/csv`} onClick={(e) => { e.preventDefault(); void download(`/api/statements/${s._id}/csv`, `${s.number}.csv`); }}>CSV</a>
                </span>
              </div>
              <div className="bill-amount">
                <span className="num">{rs(s.totals.closingOutstandingPaise)}</span>
                {s.status === 'SUPERSEDED' ? <span className="pill pill-closed">Replaced</span> : null}
              </div>
              <div className="bill-invoices"><BillInvoices relationshipId={relationshipId} statementId={s._id} /></div>
            </li>
          );
        })}
      </ul>
      {q.data && !q.data.statements.length && !(notBilledPaise && BigInt(notBilledPaise) > 0n) ? <Empty>No bills yet.</Empty> : null}
      {sp ? (
        <ConfirmDialog open={open} title="Issue this bill?" lines={[['Period', `${day(sp.start)} – ${day(sp.end)}`]]} confirmLabel="Issue bill" busy={busy} error={error} onClose={() => setOpen(false)} onConfirm={() => void run(async () => {
          const k = actionKey(`statement:${relationshipId}:${sp.start}`);
          await api.post(`/api/relationships/${relationshipId}/statements`, { periodStart: sp.start, periodEnd: sp.end }, { idempotencyKey: k.key });
          k.done();
          setOpen(false);
          await qc.invalidateQueries({ queryKey: ['statements', relationshipId] });
        })}>
          <p className="muted">The bill is a fixed copy. A later correction is issued as a new version.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/** Downloads with the active-organisation header (files are never public URLs). */
export async function download(path: string, filename: string) {
  const res = await fetch(path, { headers: { 'X-Org-Id': localStorage.getItem('si.activeOrg') ?? '' }, credentials: 'include' });
  if (!res.ok) {
    alert('Download failed. Please try again.');
    return;
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.replace(/[^\w.-]/g, '_');
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
